import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { scoreColor } from "@/lib/helpers";
import { SITE_STATUS_META, SITE_CATEGORIES } from "@/lib/siteAudit";
import { toast } from "sonner";
import { Globe, ArrowRight, HelpCircle, Loader2, AlertCircle } from "lucide-react";

export function useSiteAudit(estId) {
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/establishments/${estId}/site-audit`);
    setData(data);
    return data;
  }, [estId]);
  useEffect(() => {
    load();
    return () => clearInterval(timer.current);
  }, [load]);
  useEffect(() => {
    clearInterval(timer.current);
    if (data?.latest?.status === "running") timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [data?.latest?.status, load]);
  const start = async () => {
    try {
      await api.post(`/establishments/${estId}/site-audit`);
      toast.success("Analyse du site lancée");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return { data, load, start };
}

export function SiteAuditCard({ establishment }) {
  const navigate = useNavigate();
  const { data, start } = useSiteAudit(establishment.id);
  const latest = data?.latest;
  const running = latest?.status === "running";
  const done = latest?.status === "done" ? latest : null;

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="site-audit-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Globe className="w-5 h-5 text-blue-600" /> Audit du site</h2>
          <p className="text-xs text-slate-500 mt-0.5">Analyse réelle des pages publiques de votre site par le moteur Respondia (SEO, SEO local, technique, conversion).</p>
        </div>
        <Button onClick={start} disabled={running || !data?.can_run} data-testid="run-site-audit-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
          {running ? "Analyse en cours…" : "Analyser mon site"}
        </Button>
      </div>

      {data && !data.can_run && (
        <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="site-audit-unavailable"><HelpCircle className="w-4 h-4" />
          {data.is_demo ? "Analyse non disponible sur l'établissement de démonstration (données de démonstration uniquement)." : "Non vérifiable — renseignez l'URL du site internet de l'établissement."}</p>
      )}
      {data?.can_run && !latest && <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="site-audit-empty"><HelpCircle className="w-4 h-4" /> Aucune analyse effectuée. Lancez « Analyser mon site » pour obtenir des mesures réelles.</p>}
      {running && <div className="text-sm text-blue-700 bg-blue-50 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="site-audit-running"><Loader2 className="w-4 h-4 animate-spin" /> Exploration de {latest.url} en cours… Cette page se met à jour automatiquement.</div>}
      {latest?.status === "error" && <div className="text-sm text-red-700 bg-red-50 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="site-audit-error"><AlertCircle className="w-4 h-4" /> {latest.error}</div>}

      {done && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-slate-900 text-white" data-testid="site-audit-global">
              <div className="text-[11px] uppercase tracking-wide text-white/60">Score site</div>
              <div className="font-heading text-3xl font-extrabold">{done.global_score ?? "—"}</div>
            </div>
            {SITE_CATEGORIES.map((c) => {
              const cat = done.categories.find((x) => x.key === c.key);
              return (
                <div key={c.key} className="p-3 rounded-xl bg-slate-50" data-testid={`site-audit-score-${c.key}`}>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">{c.label}</div>
                  <div className={`font-heading text-3xl font-extrabold ${scoreColor(cat?.score)}`}>{cat?.score ?? "—"}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="site-audit-counts">
            {["CRITIQUE", "IMPORTANT", "AMELIORER", "REUSSI", "NON_VERIFIABLE"].map((s) => (
              <span key={s} className={`px-2.5 py-1 rounded-full font-semibold ${SITE_STATUS_META[s].bg} ${SITE_STATUS_META[s].text}`}>{done.counts[s] ?? 0} {SITE_STATUS_META[s].label.toLowerCase()}</span>
            ))}
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{done.pages_count} page(s) analysée(s){done.crawl_meta?.rendered_pages ? ` · ${done.crawl_meta.rendered_pages} rendue(s) en JavaScript` : ""}</span>
            <span className="text-slate-400 self-center ml-auto">Analysé le {new Date(done.finished_at).toLocaleString("fr-FR")}</span>
          </div>
          <div className="mt-4 space-y-2">
            {done.findings.filter((f) => f.status === "CRITIQUE" || f.status === "IMPORTANT").slice(0, 3).map((f) => (
              <div key={f.check_id} className="flex items-start gap-2 text-sm p-2.5 rounded-lg bg-slate-50" data-testid={`site-audit-top-${f.check_id}`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${SITE_STATUS_META[f.status].dot}`} />
                <div><span className="font-medium text-slate-800">{f.recommendation}</span> <span className="text-slate-500">— {f.observed}</span></div>
              </div>
            ))}
          </div>
          <Button variant="ghost" onClick={() => navigate("/app/site-audit")} data-testid="see-site-audit-button" className="mt-3 text-blue-600 hover:text-blue-700 px-0">
            Voir l'audit complet du site <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </>
      )}
    </div>
  );
}
